import { Capacitor } from '@capacitor/core'
import { CountryCode } from 'libphonenumber-js'
import { CarpoolType, CommunityType, DefaultCarpoolType } from 'types/CommunityTypes'

// environment
export const baseURL: string = process.env.REACT_APP_PUBLIC_API_BASEURL || 'https://dev.carpool.school'
export const isProd = (): boolean => (process.env.REACT_APP_ENV === 'prod' ? true : false)
const platform = process.env.REACT_APP_PLATFORM_EMULATED || Capacitor.getPlatform()
export const isIOS: boolean = platform === 'ios'
export const isAndroid: boolean = platform === 'android'
export const isWeb: boolean = platform === 'web'
export const isPushNotification: boolean = (isIOS || isAndroid) && Capacitor.isPluginAvailable('PushNotifications')
export const ifLocalhost = (): boolean => window.location.hostname === 'localhost'

// local store
export const setItem = (key: string, value: any) => localStorage.setItem(key, JSON.stringify(value))
export const getItem = (key: string): any => {
  const value = localStorage.getItem(key)
  return value && JSON.parse(value)
}
export const removeItem = (key: string) => localStorage.removeItem(key)
export const setItems = (sn: string, sae: string, sac: string) => {
  setItem('x-pool-sn', sn)
  setItem('x-pool-sae', sae)
  setItem('x-pool-sac', sac)
}
export const clearItems = () => {
  removeItem('x-pool-sn')
  removeItem('x-pool-sae')
  removeItem('x-pool-sac')
}

export const setSessionItem = (key: string, value: any) => sessionStorage.setItem(key, JSON.stringify(value))
export const getSessionItem = (key: string): any => {
  const value = sessionStorage.getItem(key)
  return value && JSON.parse(value)
}
export const removeSessionItem = (key: string) => sessionStorage.removeItem(key)

// signed in
export const ifAdminSignedIn = (): boolean => !!getItem('x-pool-sac')
export const ifSignedIn = (): boolean => !!getItem('tokens')?.IdToken

// jwt token
export const parseJwt = (token: string) => {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join(''),
  )

  return JSON.parse(jsonPayload)
}

/**
 * Label format options.
 */
type LabelFormat =
  | 'lowercase' // e.g., school
  | 'lowercasePlural' // e.g., schools
  | 'lowercasePossessive' // e.g., school's
  | 'lowercasePluralPossessive' // e.g., schools'
  | 'PascalCase' // e.g., School
  | 'PascalCasePlural' // e.g., Schools
  | 'PascalCasePossessive' // e.g., School's
  | 'PascalCasePluralPossessive' // e.g., Schools'
type LabelSet = { [key in LabelFormat]: string }
type CarpoolTypeLabelSet = { [key in CarpoolType]: LabelSet }
type CommunityLabelSet = LabelSet & { carpoolType?: CarpoolTypeLabelSet }
type AppLabels = { [key in CommunityType]: CommunityLabelSet }
export const LABELS: AppLabels = {
  [CommunityType.SCHOOL]: {
    lowercase: 'school',
    lowercasePlural: 'schools',
    lowercasePossessive: "school's",
    lowercasePluralPossessive: "schools'",
    PascalCase: 'School',
    PascalCasePlural: 'Schools',
    PascalCasePossessive: "School's",
    PascalCasePluralPossessive: "Schools'",
    carpoolType: {
      [DefaultCarpoolType.ARRIVAL]: {
        lowercase: 'morning',
        lowercasePlural: 'mornings',
        lowercasePossessive: "morning's",
        lowercasePluralPossessive: "mornings'",
        PascalCase: 'Morning',
        PascalCasePlural: 'Mornings',
        PascalCasePossessive: "Morning's",
        PascalCasePluralPossessive: "Mornings'",
      },
      [DefaultCarpoolType.DISMISSAL]: {
        lowercase: 'afternoon',
        lowercasePlural: 'afternoons',
        lowercasePossessive: "afternoon's",
        lowercasePluralPossessive: "afternoons'",
        PascalCase: 'Afternoon',
        PascalCasePlural: 'Afternoons',
        PascalCasePossessive: "Afternoon's",
        PascalCasePluralPossessive: "Afternoons'",
      },
    },
  },
  [CommunityType.LEA]: {
    lowercase: 'district',
    lowercasePlural: 'districts',
    lowercasePossessive: "district's",
    lowercasePluralPossessive: "districts'",
    PascalCase: 'District',
    PascalCasePlural: 'Districts',
    PascalCasePossessive: "District's",
    PascalCasePluralPossessive: "Districts'",
  },
}

/**
 * Formats a label based on the format.
 *
 * @param value - The value to format.
 * @param format - The format to use.
 * @returns The formatted label.
 */
export const formatLabel = (value: string, format: LabelFormat): string => {
  const lower = value.toLowerCase()
  switch (format) {
    case 'lowercase':
      return lower
    case 'lowercasePlural':
      return `${lower}s`
    case 'lowercasePossessive':
      return `${lower}'s`
    case 'lowercasePluralPossessive':
      return `${lower}s'`
    case 'PascalCase':
      return lower.charAt(0).toUpperCase() + lower.slice(1)
    case 'PascalCasePlural':
      return `${lower.charAt(0).toUpperCase() + lower.slice(1)}s`
    case 'PascalCasePossessive':
      return `${lower.charAt(0).toUpperCase() + lower.slice(1)}'s`
    case 'PascalCasePluralPossessive':
      return `${lower.charAt(0).toUpperCase() + lower.slice(1)}s'`
  }
}

// schoolname
export const legacySchoolsWithoutPlaceId = [
  'carnage middle',
  'franklin academy',
  'fuller elementary',
  'moore square middle',
]
export const titleCase = (str: string) => {
  const splitStr = str.toLowerCase().split(' ')
  for (let i = 0; i < splitStr.length; i++) {
    splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1)
  }
  return splitStr.join(' ')
}

/**
 * A simple helper function for pluralization.
 * @param count The number of items.
 * @param singular The singular form of the word.
 * @returns The count and the correct singular or plural word.
 */
export const pluralize = (count: number, singular: string): string => {
  return `${count} ${count === 1 ? singular : singular + 's'}`
}

export const titleCaseSchoolNameForUI = (str: string) => {
  const endIdx = str.lastIndexOf('-') > 0 ? str.lastIndexOf('-') : str.length
  return titleCase(str.slice(0, endIdx))
}

/**
 * Generates a simple, Docker-style random name for a carpool group chat.
 * @returns A randomly generated name like "Happy_Riders".
 */
export const generateCarpoolGroupName = (): string => {
  // --- Vocabulary Lists ---

  const adjectives = [
    'Agile',
    'Alert',
    'Awesome',
    'Bold',
    'Brave',
    'Bright',
    'Brilliant',
    'Calm',
    'Caring',
    'Clever',
    'Cool',
    'Creative',
    'Dandy',
    'Daring',
    'Dashing',
    'Eager',
    'Early',
    'Easy',
    'Eco',
    'Epic',
    'Fancy',
    'Fast',
    'Fearless',
    'First',
    'Focused',
    'Friendly',
    'Frosty',
    'Funny',
    'Gentle',
    'Golden',
    'Great',
    'Green',
    'Happy',
    'Honest',
    'Humble',
    'Jolly',
    'Joyful',
    'Keen',
    'Kind',
    'Laughing',
    'Lively',
    'Lucky',
    'Magic',
    'Mellow',
    'Mighty',
    'Mint',
    'Modern',
    'Nice',
    'Noble',
    'Peaceful',
    'Polite',
    'Proud',
    'Punctual',
    'Quick',
    'Quiet',
    'Quirky',
    'Ready',
    'Relaxed',
    'Royal',
    'Sharp',
    'Shining',
    'Silly',
    'Smart',
    'Smiling',
    'Smooth',
    'Snowy',
    'Speedy',
    'Stellar',
    'Stoked',
    'Super',
    'Sunny',
    'Swift',
    'Thoughtful',
    'Thriving',
    'Tidy',
    'True',
    'Upbeat',
    'Vibrant',
    'Vivid',
    'Warm',
    'Wise',
    'Witty',
    'Zen',
    'Zesty',
  ]

  const nouns = [
    'Alliance',
    'Ambassadors',
    'Archers',
    'Band',
    'Brigade',
    'Caravan',
    'Chariot',
    'Circle',
    'Club',
    'Collective',
    'Comets',
    'Community',
    'Convoy',
    'Couriers',
    'Crew',
    'Cruisers',
    'Dashers',
    'Dream',
    'Drivers',
    'Eagles',
    'Explorers',
    'Express',
    'Falcons',
    'Fellowship',
    'Fleet',
    'Flight',
    'Force',
    'Friends',
    'Gliders',
    'Group',
    'Guardians',
    'Guild',
    'Haulers',
    'Helpers',
    'Heroes',
    'Hive',
    'Huddle',
    'Hustlers',
    'Jockeys',
    'Journey',
    'League',
    'Lions',
    'Movers',
    'Navigators',
    'Network',
    'Pack',
    'Pact',
    'Patrol',
    'Pilots',
    'Pioneers',
    'Poolers',
    'Quest',
    'Racers',
    'Riders',
    'Roadsters',
    'Rockets',
    'Runners',
    'Shuttle',
    'Squad',
    'Stars',
    'Team',
    'Travelers',
    'Trek',
    'Tribe',
    'Trip',
    'Troop',
    'Unit',
    'United',
    'Vanguard',
    'Venturers',
    'Voyagers',
    'Wayfinders',
    'Wing',
    'Wizards',
  ]

  const randomAdjective = adjectives[Math.floor(Math.random() * adjectives.length)]
  const randomNoun = nouns[Math.floor(Math.random() * nouns.length)]

  // The name is now just the simple, random combination
  return `${randomAdjective} ${randomNoun}`
}

// phoneNumber
export const defaultPhoneNumberCountry: CountryCode = 'US'

// disclaimer text
export const disclaimerText: string = `Carpool.School does not verify drivers' licenses or insurance coverage. Users are responsible for screening all participants of carpool. `

// tooltips
export const phoneCodeTooltip = (): string => 'Enter 6 digit code sent to your phone number.'
export const emailCodeTooltip = (): string => 'Enter 6 digit code sent to your email.'
export const phoneNumberTooltip = (): string => 'A valid 10-digit mobile number is required for SMS'
export const phoneNumberRatesTooltip = (): string => 'Message and data rates may apply.'
export const passwordTooltip = (): string => 'Password must be atleast 8 characters.'
export const reenterPasswordTooltip = (): string => 'Passwords do not match.'
export const emptyState = (): string => '-- Not Set --'
export const schoolSubscriptionLabel = (v: boolean): string =>
  v ? 'School subscription enabled' : 'Enable school-wide subscription'
export const userSubscriptionLabel = (v: boolean): string => (v ? 'Subscription enabled' : 'Enable subscription')
export const userSubscriptionTooltip = (v: boolean): string =>
  v ? '' : 'Subscribe today to connect with other families'
export const commentTooltip = (): string => 'Maximum 500 characters.'

// limits of school admins, schedule groups, room admins, and carpool updates
export const MAX_ALLOWED_REGISTRATIONS_PER_USER: number = 6
export const MIN_SCHEDULE_GROUPS: number = 1
export const MAX_SCHEDULE_GROUPS: number = 5
export const MIN_SCHOOL_ADMINS: number = 1
export const MAX_SCHOOL_ADMINS: number = 10
export const MIN_ROOM_ADMINS: number = 1
export const MAX_ROOM_ADMINS: number = 10
export const MAX_ALLOWED_CARPOOL_UPDATES_PER_REQUEST: number = 25

// date
/**
 * Returns a date string in ISO format with time set to midnight (00:00:00).
 * @param date The date to create an ISO string for.  Defaults to the current date.
 * @returns A new Date object with time set to midnight.
 */
export const getUTCDateAtMidnightISO = (date: Date = new Date()): string => {
  const newDate = new Date(date)
  newDate.setHours(0)
  newDate.setMinutes(0)
  newDate.setSeconds(0)
  newDate.setMilliseconds(0)
  return newDate.toISOString()
}
export const getLocalDate = (date: Date = new Date()): string => getUTCDateAtMidnightISO(date).split('T')[0]
export const getLocaleDate = (): string =>
  new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  })

/**
 * Returns the calendar's minimum and maximum selectable dates.
 * Range: July 4th of previous year to July 4th of next year.
 */
export const getCalendarDateRange = (): { startDate: Date; endDate: Date } => {
  const currentYear = new Date().getFullYear()
  return {
    startDate: new Date(currentYear - 1, 6, 4), // July 4th of previous year
    endDate: new Date(currentYear + 1, 6, 4), // July 4th of next year
  }
}

export const getTimeNumber = (date: Date): number => {
  return date.getHours() * 100 + date.getMinutes()
}

export const getFullDate = (date: Date): string => {
  const year: string = date.getFullYear().toString()
  const month: string = (date.getMonth() + 1).toString().padStart(2, '0')
  const day: string = date.getDate().toString().padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getTimeString = (time?: number): string => {
  if (time === undefined || time < 0 || time > 2400) return ''
  let amPm: string = 'AM'
  let hr: number = Math.floor(time / 100)
  if (hr >= 12) {
    if (hr > 12) hr -= 12
    amPm = 'PM'
  }
  const mn: string = (time % 100).toLocaleString(undefined, { minimumIntegerDigits: 2 })
  return `${hr}:${mn} ${amPm}`
}

export const getTimeStringFromNumber = (time?: number): string => {
  if (time === undefined || time < 0 || time > 2400) return '00:00'
  const hr: string = Math.floor(time / 100).toLocaleString(undefined, { minimumIntegerDigits: 2 })
  const mn: string = (time % 100).toLocaleString(undefined, { minimumIntegerDigits: 2 })
  return `${hr}:${mn}`
}

export const getTimeNumberFromString = (time?: string): number => {
  const regEx = /^\d{2}:\d{2}$/
  if (!time || !time.match(regEx)) return 0
  return Number(time.slice(0, 2)) * 100 + Number(time.slice(3, 5))
}

export const isDateValid = (date?: string): boolean => {
  const regEx = /^\d{4}-\d{2}-\d{2}/
  if (!date || !date.match(regEx)) return false // Invalid format
  const d = new Date(date)
  const dNum = d.getTime()
  if (!dNum && dNum !== 0) return false // NaN value, Invalid date
  return d.toISOString().slice(0, date.length) === date
}

export const isDateInLastMonth = (d?: string): boolean => {
  return d ? new Date(d).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000 : false
}

// map
export const METERS_TO_MILES: number = 0.000621371192

// billing
export const isBillingDisabledForSchool: boolean = false
export const isBillingDisabledForFamily: boolean = true
export const MIN_REQUIRED_REGISTRATIONS: number = 1
export const MAX_ALLOWED_REGISTRATIONS: number = 500

// paid transport
export const isPaidTransportFeatureEnabled: boolean = false

// notifications
export const MAX_ALLOWED_ENROLLMENT_INVITES_PER_BATCH: number = 100
